import React, { useEffect, useState } from 'react';
import { UtensilsCrossed } from 'lucide-react';
import { resolveImageUrl } from '@/lib/img';

/**
 * SafeImage — renders an image with three DISTINCT visual states:
 *
 *   1. LOADING  (src === undefined)
 *        Parent has not yet provided a source (typically API data still loading).
 *        Renders a neutral, transparent container so the parent's own background
 *        shows through. NO branded fallback icon. NO layout shift.
 *
 *   2. MISSING  (src === null | "" | image errors + no fallback)
 *        Item genuinely has no image, or the URL failed to load.
 *        Renders the branded Smithy Browns silhouette.
 *
 *   3. REAL     (src is a URL)
 *        Renders the <img> immediately with the real URL. While the browser
 *        downloads the file, the neutral container is visible behind it. On
 *        successful decode we fade the image in; on error we fall through to
 *        MISSING (or `fallbackSrc` if provided).
 *
 * Props:
 *   src            — string | null | undefined
 *   fallbackSrc    — optional secondary URL tried before the branded silhouette
 *   alt, className, imgClassName, fallbackIconSize — presentational
 */
export const SafeImage = ({
  src,
  fallbackSrc,
  alt = '',
  className = '',
  imgClassName = '',
  fallbackIconSize = 32,
}) => {
  const [primaryErrored, setPrimaryErrored] = useState(false);
  const [fallbackErrored, setFallbackErrored] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const resolvedPrimary = resolveImageUrl(src);
  const resolvedFallback = resolveImageUrl(fallbackSrc);

  // Reset internal state whenever the source changes so switching from a
  // loading state to a real URL (and vice-versa) behaves predictably.
  useEffect(() => {
    setPrimaryErrored(false);
    setLoaded(false);
  }, [resolvedPrimary]);
  useEffect(() => { setFallbackErrored(false); }, [resolvedFallback]);

  const isLoadingUpstream = src === undefined;
  const primaryOk = !!resolvedPrimary && !primaryErrored;
  const fallbackOk = !!resolvedFallback && !fallbackErrored;

  // 1) LOADING — parent hasn't decided on a source yet. Neutral container only.
  if (isLoadingUpstream) {
    return (
      <div
        className={className}
        aria-hidden="true"
        data-testid="safe-image-loading"
      />
    );
  }

  // 2) MISSING — parent explicitly passed null/empty and no fallback URL, OR
  //    both primary and fallback URLs have errored. Show branded silhouette.
  if (!primaryOk && !fallbackOk) {
    return (
      <div
        className={`${className} bg-black grid place-items-center`}
        aria-hidden={alt ? undefined : 'true'}
        data-testid="safe-image-fallback"
      >
        <div className="flex flex-col items-center gap-1 text-white/25">
          <UtensilsCrossed size={fallbackIconSize} />
          <span className="text-[10px] uppercase tracking-widest">Smithy Browns</span>
        </div>
      </div>
    );
  }

  // 3) REAL — render <img>. While it downloads the parent bg shows through; fade in on load.
  const activeSrc = primaryOk ? resolvedPrimary : resolvedFallback;
  return (
    <img
      src={activeSrc}
      alt={alt}
      loading="lazy"
      decoding="async"
      onLoad={() => setLoaded(true)}
      onError={() => (primaryOk ? setPrimaryErrored(true) : setFallbackErrored(true))}
      style={{ opacity: loaded ? 1 : 0, transition: 'opacity 200ms ease-out' }}
      className={imgClassName || className}
    />
  );
};
